import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { assetsAPI } from '@/lib/api-client'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { AssetFormDialog } from './AssetFormDialog'
import {
  ArrowLeft,
  Edit,
  Trash2,
  Package,
  Loader2,
  DollarSign,
  TrendingUp,
  Tag,
  ShoppingBag,
  ArrowRightLeft,
  CheckCircle,
  XCircle,
} from 'lucide-react'

const STATUS_LABELS: Record<string, string> = {
  EM_POSSE: 'Em Posse',
  A_VENDA: 'A Venda',
  VENDIDO: 'Vendido',
  UTILIZADO: 'Utilizado',
}

const STATUS_COLORS: Record<string, string> = {
  EM_POSSE: 'bg-blue-100 text-blue-700',
  A_VENDA: 'bg-amber-100 text-amber-700',
  VENDIDO: 'bg-green-100 text-green-700',
  UTILIZADO: 'bg-purple-100 text-purple-700',
}

const CATEGORY_LABELS: Record<string, string> = {
  VEHICLE: 'Veiculo',
  PROPERTY: 'Imovel',
  LAND: 'Terreno',
  HOUSE: 'Casa',
  OTHER: 'Outro',
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString('pt-BR')
}

export function AssetDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const canEdit = usePermission('assets:edit')
  const canDelete = usePermission('assets:delete')

  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showSellDialog, setShowSellDialog] = useState(false)
  const [sellValue, setSellValue] = useState('')
  const [sellNotes, setSellNotes] = useState('')
  const [sellCreateTransaction, setSellCreateTransaction] = useState(true)

  const { data: asset, isLoading } = useQuery({
    queryKey: ['asset', id],
    queryFn: () => assetsAPI.getById(id!),
    enabled: !!id,
  })

  const deleteMutation = useMutation({
    mutationFn: () => assetsAPI.delete(id!),
    onSuccess: (data: any) => {
      toast.success(data.message)
      queryClient.invalidateQueries({ queryKey: ['assets'] })
      navigate('/assets')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const changeStatusMutation = useMutation({
    mutationFn: (data: any) => assetsAPI.changeStatus(id!, data),
    onSuccess: () => {
      toast.success('Status do ativo atualizado!')
      queryClient.invalidateQueries({ queryKey: ['assets'] })
      queryClient.invalidateQueries({ queryKey: ['asset', id] })
      setShowSellDialog(false)
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const handleSell = () => {
    const value = Number(sellValue)
    if (!value || value <= 0) {
      toast.error('Informe o valor de venda')
      return
    }
    changeStatusMutation.mutate({
      status: 'VENDIDO',
      liquidationValue: value,
      liquidationNotes: sellNotes || undefined,
      createTransaction: sellCreateTransaction,
    })
  }

  const handleQuickStatusChange = (status: string) => {
    if (status === 'VENDIDO') {
      setSellValue('')
      setSellNotes('')
      setSellCreateTransaction(true)
      setShowSellDialog(true)
      return
    }
    changeStatusMutation.mutate({ status })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!asset) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <h2 className="text-xl font-medium text-neutral-900">
          Ativo nao encontrado
        </h2>
        <Button className="mt-4" onClick={() => navigate('/assets')}>
          Voltar
        </Button>
      </div>
    )
  }

  const isTerminal = asset.status === 'VENDIDO' || asset.status === 'UTILIZADO'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/assets')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <Package className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold text-neutral-900">
                {asset.name}
              </h1>
              <Badge
                variant="outline"
                className={STATUS_COLORS[asset.status] || ''}
              >
                {STATUS_LABELS[asset.status] || asset.status}
              </Badge>
              <Badge variant="outline">
                {CATEGORY_LABELS[asset.category] || asset.category}
              </Badge>
            </div>
            {asset.project && (
              <p className="mt-1 text-neutral-500">
                Projeto: {asset.project.name}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canEdit && !isTerminal && (
            <Button variant="outline" onClick={() => setShowEditDialog(true)}>
              <Edit className="mr-2 h-4 w-4" />
              Editar
            </Button>
          )}
          {canDelete && !isTerminal && (
            <Button
              variant="outline"
              className="text-red-600 hover:text-red-700"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Excluir
            </Button>
          )}
        </div>
      </div>

      {/* Status Actions */}
      {canEdit && !isTerminal && (
        <Card>
          <CardContent className="flex items-center gap-3 pt-6">
            <span className="text-sm font-medium text-neutral-700">Acoes:</span>
            {asset.status === 'EM_POSSE' && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  onClick={() => handleQuickStatusChange('A_VENDA')}
                  disabled={changeStatusMutation.isPending}
                >
                  <Tag className="mr-2 h-4 w-4" />
                  Colocar a Venda
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-green-700 border-green-300 hover:bg-green-50"
                  onClick={() => handleQuickStatusChange('VENDIDO')}
                  disabled={changeStatusMutation.isPending}
                >
                  <DollarSign className="mr-2 h-4 w-4" />
                  Registrar Venda
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-purple-700 border-purple-300 hover:bg-purple-50"
                  onClick={() => handleQuickStatusChange('UTILIZADO')}
                  disabled={changeStatusMutation.isPending}
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Marcar como Utilizado
                </Button>
              </>
            )}
            {asset.status === 'A_VENDA' && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleQuickStatusChange('EM_POSSE')}
                  disabled={changeStatusMutation.isPending}
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Voltar Em Posse
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-green-700 border-green-300 hover:bg-green-50"
                  onClick={() => handleQuickStatusChange('VENDIDO')}
                  disabled={changeStatusMutation.isPending}
                >
                  <DollarSign className="mr-2 h-4 w-4" />
                  Registrar Venda
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-purple-700 border-purple-300 hover:bg-purple-50"
                  onClick={() => handleQuickStatusChange('UTILIZADO')}
                  disabled={changeStatusMutation.isPending}
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Marcar como Utilizado
                </Button>
              </>
            )}
            {changeStatusMutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Informacoes</TabsTrigger>
          <TabsTrigger value="origin">Origem</TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info" className="space-y-6">
          {/* Value Cards */}
          <div className="grid gap-6 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-neutral-500">
                  Valor de Aquisicao
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-amber-600" />
                  <span className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(asset.acquisitionValue)}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-neutral-500">
                  Valor de Mercado
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  <span className="text-2xl font-bold text-neutral-900">
                    {asset.marketValue
                      ? formatCurrency(asset.marketValue)
                      : 'Nao informado'}
                  </span>
                </div>
              </CardContent>
            </Card>

            {asset.liquidationValue && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-neutral-500">
                    Valor de Liquidacao
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <ShoppingBag className="h-5 w-5 text-green-600" />
                    <span className="text-2xl font-bold text-neutral-900">
                      {formatCurrency(asset.liquidationValue)}
                    </span>
                  </div>
                  {asset.liquidationValue !== asset.acquisitionValue && (
                    <p className={`text-xs mt-1 ${asset.liquidationValue > asset.acquisitionValue ? 'text-green-600' : 'text-red-600'}`}>
                      {asset.liquidationValue > asset.acquisitionValue ? '+' : ''}
                      {formatCurrency(asset.liquidationValue - asset.acquisitionValue)}
                      {' '}({asset.liquidationValue > asset.acquisitionValue ? 'lucro' : 'prejuizo'})
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Detalhes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {asset.description && (
                <div>
                  <span className="text-sm font-medium text-neutral-500">Descricao</span>
                  <p className="text-sm text-neutral-900 mt-1">{asset.description}</p>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <span className="text-sm font-medium text-neutral-500">Categoria</span>
                  <p className="text-sm text-neutral-900 mt-1">
                    {CATEGORY_LABELS[asset.category] || asset.category}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-neutral-500">Origem</span>
                  <p className="text-sm text-neutral-900 mt-1">
                    {asset.origin === 'PERMUTA' ? 'Permuta (automatico)' : 'Cadastro manual'}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-neutral-500">Data de Aquisicao</span>
                  <p className="text-sm text-neutral-900 mt-1">{formatDate(asset.acquiredAt)}</p>
                </div>
                {asset.liquidatedAt && (
                  <div>
                    <span className="text-sm font-medium text-neutral-500">Data de Liquidacao</span>
                    <p className="text-sm text-neutral-900 mt-1">{formatDate(asset.liquidatedAt)}</p>
                  </div>
                )}
              </div>

              {asset.liquidationNotes && (
                <div>
                  <span className="text-sm font-medium text-neutral-500">Notas da Liquidacao</span>
                  <p className="text-sm text-neutral-900 mt-1">{asset.liquidationNotes}</p>
                </div>
              )}

              {asset.project && (
                <div>
                  <span className="text-sm font-medium text-neutral-500">Projeto</span>
                  <p className="text-sm text-neutral-900 mt-1">{asset.project.name}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Origin Tab */}
        <TabsContent value="origin" className="space-y-6">
          {asset.origin === 'PERMUTA' && asset.salePermuta ? (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ArrowRightLeft className="h-5 w-5 text-amber-500" />
                  <CardTitle className="text-base">Origem da Permuta</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-neutral-600">
                  Este ativo foi recebido como parte de pagamento (permuta) em uma venda.
                </p>

                <div className="grid gap-4 sm:grid-cols-2">
                  {asset.salePermuta.sale?.clientName && (
                    <div>
                      <span className="text-sm font-medium text-neutral-500">Comprador</span>
                      <p className="text-sm text-neutral-900 mt-1">
                        {asset.salePermuta.sale.clientName}
                      </p>
                    </div>
                  )}
                  {asset.salePermuta.sale?.unit?.code && (
                    <div>
                      <span className="text-sm font-medium text-neutral-500">Unidade</span>
                      <p className="text-sm text-neutral-900 mt-1">
                        {asset.salePermuta.sale.unit.code}
                      </p>
                    </div>
                  )}
                  {asset.salePermuta.sale?.saleDate && (
                    <div>
                      <span className="text-sm font-medium text-neutral-500">Data da Venda</span>
                      <p className="text-sm text-neutral-900 mt-1">
                        {formatDate(asset.salePermuta.sale.saleDate)}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-sm font-medium text-neutral-500">Valor da Permuta</span>
                    <p className="text-sm font-medium text-amber-600 mt-1">
                      {formatCurrency(asset.salePermuta.value)}
                    </p>
                  </div>
                </div>

                {asset.salePermuta.sale?.buyerId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/buyers/${asset.salePermuta.sale.buyerId}`)}
                  >
                    Ver Comprador
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Cadastro Manual</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-neutral-500">
                  Este ativo foi cadastrado manualmente, sem vinculo com uma permuta de venda.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      {showEditDialog && (
        <AssetFormDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          asset={asset}
        />
      )}

      {/* Sell Dialog */}
      <Dialog open={showSellDialog} onOpenChange={setShowSellDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Venda do Ativo</DialogTitle>
            <DialogDescription>
              Informe o valor de venda e detalhes da liquidacao.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="sellValue">Valor de Venda *</Label>
              <Input
                id="sellValue"
                type="number"
                step="0.01"
                value={sellValue}
                onChange={(e) => setSellValue(e.target.value)}
                placeholder="0,00"
              />
            </div>
            <div>
              <Label htmlFor="sellNotes">Observacoes</Label>
              <Textarea
                id="sellNotes"
                value={sellNotes}
                onChange={(e) => setSellNotes(e.target.value)}
                placeholder="Ex: Vendido para Joao Silva"
                rows={3}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="createTransaction"
                checked={sellCreateTransaction}
                onChange={(e) => setSellCreateTransaction(e.target.checked)}
              />
              <Label htmlFor="createTransaction" className="text-sm font-normal">
                Criar transacao financeira automaticamente
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSellDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSell}
              disabled={changeStatusMutation.isPending}
            >
              {changeStatusMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirmar Venda
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir Ativo</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o ativo{' '}
              <strong>{asset.name}</strong>? Esta acao nao pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
